import { useMemo, useState } from "react";
import { Chip, Tooltip, ToggleButton, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SmsIcon from "@mui/icons-material/Sms";
import withRole from "../../Hooks/withRole";
import MUIDataTableCustomStyle from "../MUIDataTableCustomStyle";
import Filters from "../Widgets/Filters";
import GenericModal from "../GenericModal";
import genericStyles from "../../Styles/genericStyles.module.css";
import { endpoints, localUrlEnum } from "../../Utils/UrlEnum";
import { Vocabulary } from "../../Utils/Vocabulary";
import { EComponentTypes, ETableEditAction } from "../../Utils/Enums";
import { formatDate } from "../../Utils/Utils";
import Config from "../../Utils/Config";
import * as yup from "yup";

function SmsSentReportTable() {
  const [selectedMessage, setSelectedMessage] = useState<string | null>(null);

  const filters = [
    {
      property: "start",
      label: Vocabulary.startDate,
      type: EComponentTypes.DATE_PICKER,
      validation: yup.string().required(Vocabulary.requiredField),
      defaultValue: Config.defaultStartDate,
    },
    {
      property: "end",
      label: Vocabulary.endDate,
      type: EComponentTypes.DATE_PICKER,
      validation: yup.string().required(Vocabulary.requiredField),
      defaultValue: Config.defaultEndDate,
    },
  ];

  const columns = useMemo(
    () => [
      {
        name: "sentByName",
        label: Vocabulary.sentBy,
      },
      {
        name: "intendedFor",
        label: Vocabulary.intendedFor,
      },
      {
        name: "clientName",
        label: Vocabulary.client,
      },
      {
        name: "phoneNumber",
        label: Vocabulary.phone,
      },
      {
        name: "smsText",
        label: Vocabulary.message,
        options: {
          customBodyRender: (value: string) =>
            value?.length > 60 ? `${value.substring(0, 60)}...` : value,
          exportValue: (value: string) => value ?? "",
        },
      },
      {
        name: "success",
        label: Vocabulary.status,
        options: {
          sort: false,
          customBodyRender: (value: boolean) => (
            <Chip
              label={value ? Vocabulary.smsSent : Vocabulary.smsError}
              color={value ? "success" : "error"}
              size="small"
            />
          ),
          exportValue: (value: boolean) =>
            value ? Vocabulary.smsSent : Vocabulary.smsError,
        },
      },
      {
        name: "errorMessage",
        label: Vocabulary.errorMessage,
        options: {
          customBodyRender: (value: string, tableMeta: any) => {
            const success: boolean = tableMeta.rowData[5];
            return !success && value ? value : null;
          },
          exportValue: (value: string, tableMeta: any) => {
            const success: boolean = tableMeta?.rowData?.[5];
            return !success && value ? value : "";
          },
        },
      },
      {
        name: "createdAt",
        label: Vocabulary.createdAt,
        options: {
          customBodyRender: (value: Date) =>
            formatDate(value, "dd.MM.yyyy HH:mm"),
          exportValue: (value: Date) =>
            value ? formatDate(value, "dd.MM.yyyy HH:mm") : "",
        },
      },
    ],
    []
  );

  return (
    <div className={genericStyles.mainContainer}>
      <Filters filters={filters} defaultOpen />
      <MUIDataTableCustomStyle
        title={""}
        CRUDEndPoint={endpoints.smsSentReport.list}
        columns={columns}
        onEditAction={ETableEditAction.NONE}
        hideAddButton
        hideDeleteButton
        hideEditButton
        showPrintButton
        showDownloadButton
        exportWithTimestamp
        renderCustomColumns={(_rowIndex: number, row: any) => (
          <>
            <Tooltip title={Vocabulary.message}>
              <ToggleButton
                value="message"
                aria-label="message"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedMessage(row.smsText ?? "");
                }}
              >
                <SmsIcon />
              </ToggleButton>
            </Tooltip>
            {row.orderId && (
              <Tooltip title={Vocabulary.order}>
                <ToggleButton
                  value="order"
                  aria-label="order"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`${localUrlEnum.order}/${row.orderId}`, "_blank");
                  }}
                >
                  <OpenInNewIcon />
                </ToggleButton>
              </Tooltip>
            )}
          </>
        )}
      />
      <GenericModal
        title={Vocabulary.message}
        open={!!selectedMessage}
        onClose={() => setSelectedMessage(null)}
      >
        <Typography style={{ whiteSpace: "pre-wrap" }}>
          {selectedMessage}
        </Typography>
      </GenericModal>
    </div>
  );
}

export default withRole(SmsSentReportTable);
